/**
 * Session state management for DEX UI.
 * Bridges the state store and browser storage.
 */

import { setState, getState, batchUpdate } from './store.js';
import { storage } from '../utils/storage.js';
import { getRecordedActions, clearRecording } from './macro.js';

/**
 * Initialize session state from browser storage.
 * Call this on app startup to restore previous session if available.
 * @returns {boolean} Whether a session was restored
 */
export function initSession() {
  const restorable = storage.getRestorableSession();

  if (restorable) {
    // We have a stored key - set it in state but DON'T mark as authenticated yet
    // Authentication will be validated via API call
    batchUpdate({
      'session.apiKey': restorable.key,
      'session.rememberMe': restorable.rememberMe
    });
    return true;
  }

  // Load stored keys for the key picker (even if no active session)
  const storedKeys = storage.getKeys();
  if (storedKeys.length > 0) {
    setState('storedKeys', storedKeys);
  }

  return false;
}

/**
 * Set the session as authenticated after successful API validation.
 * @param {Object} authData - {apiKey, role, partyId, rememberMe}
 */
export function setAuthenticated(authData) {
  const { apiKey, role, partyId, rememberMe } = authData;

  // Update state
  batchUpdate({
    'session.apiKey': apiKey,
    'session.role': role,
    'session.partyId': partyId,
    'session.authenticated': true,
    'session.rememberMe': rememberMe,
    'ui.loading': false,
    'ui.error': null,
    'ui.loginExpanded': false
  });

  // Persist to storage
  storage.setActiveKey(apiKey, rememberMe);
  storage.saveKey({ key: apiKey, role, partyId });

  // Update stored keys in state
  setState('storedKeys', storage.getKeys());
}

/**
 * Clear the current session (logout).
 * Clears ALL stored keys per user decision requirements.
 * Prompts user to export recorded macro actions if any exist.
 * @returns {boolean} Whether logout completed (false if cancelled to export)
 */
export function clearSession() {
  // Check for recorded macro actions
  const actions = getRecordedActions();
  if (actions.length > 0) {
    const shouldExport = window.confirm(
      'You have recorded macro actions that will be lost.\n\n' +
      'Click OK to open the preview (you can export from there),\n' +
      'or Cancel to discard and continue logging out.'
    );
    if (shouldExport) {
      document.dispatchEvent(new CustomEvent('macro:preview'));
      return false; // Signal that logout was cancelled
    }
    // User chose to discard - clear macro state
    clearRecording();
  }

  // Clear storage first
  storage.clearAll();

  // Reset state
  batchUpdate({
    'session.apiKey': null,
    'session.role': null,
    'session.partyId': null,
    'session.authenticated': false,
    'session.rememberMe': false,
    'storedKeys': [],
    'ui.loading': false,
    'ui.error': null,
    'ui.loginExpanded': false,
    'ui.showTimeoutWarning': false
  });

  return true; // Signal that logout completed
}

/**
 * Set authentication error state.
 * @param {string} error - Error message to display
 */
export function setAuthError(error) {
  batchUpdate({
    'ui.loading': false,
    'ui.error': error
  });
}

/**
 * Set loading state during authentication.
 * @param {boolean} loading - Whether auth is in progress
 */
export function setLoading(loading) {
  setState('ui.loading', loading);
}

/**
 * Toggle the login form expanded state.
 */
export function toggleLoginExpanded() {
  const { ui } = getState();
  setState('ui.loginExpanded', !ui.loginExpanded);
}

/**
 * Toggle API key visibility in form.
 */
export function toggleKeyVisibility() {
  const { ui } = getState();
  setState('ui.showKey', !ui.showKey);
}

/**
 * Show/hide timeout warning modal.
 * @param {boolean} show - Whether to show the warning
 */
export function setTimeoutWarning(show) {
  setState('ui.showTimeoutWarning', show);
}

/**
 * Switch to a different stored key.
 * Sets the key in state for validation - caller must then authenticate.
 * @param {string} key - The API key to switch to
 */
export function switchToKey(key) {
  const storedKeys = storage.getKeys();
  const keyData = storedKeys.find(k => k.key === key);

  if (keyData) {
    // Set key in state - authentication happens via API call
    batchUpdate({
      'session.apiKey': key,
      'session.authenticated': false, // Will be set true after validation
      'ui.loading': true,
      'ui.error': null
    });
    return keyData;
  }
  return null;
}
